import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { IdentityProvider, SmileIdImage } from '../../model/kamonal-model';
import { MetaMapType } from '../../../core/models/onboarding/onboarding-v2-model';

@Component({
    selector: 'app-kamona-liveliness-widget',
    templateUrl: './kamona-liveliness-widget.component.html',
    styleUrls: ['./kamona-liveliness-widget.component.scss'],
})
export class KamonaLivelinessWidgetComponent implements OnInit {
    @Input() metaMapReference: string;
    @Input() useWebSdk = true;
    @Input() flowId: string;
    @Output() emitBack = new EventEmitter();
    @Input() btnText = 'Continue';
    @Output() emitCancel = new EventEmitter();
    @Output() emitFinishedMetamapVerification = new EventEmitter();
    @Output() emitFinishedSmileIDVerification = new EventEmitter();
    @Input() usage = '';
    @Input() identityProvider = IdentityProvider.METAMAP;
    @Input() useSubmitBtnOnly: boolean;
    @Input() submitting: boolean;
    title = 'Face Verification';
    requestCameraAccessBtnHTMLElement: HTMLElement;
    startImageCaptureBtnHTMLElement: HTMLElement;
    restartImageCaptureBtnHTMLElement: HTMLElement;
    selectSelfieBtnHTMLElement: HTMLElement;
    readonly REQUEST_CAMERA_ACCESS = 'Request Camera Access';
    readonly TAKE_SELFIE = 'Take Selfie';
    readonly RE_TAKE_SELFIE = 'Re-take selfie';
    readonly SELECTED_SELFIE = ' Yes, use this one';
    useSmileID: boolean;
    hideUIElement: boolean;
    metaData: any;
    isReview: boolean;
    @Input() styleClass = {
        'max-w-[468px] py-[32px] flex flex-col gap-[36px] px-[48px] rounded-[16px] bg-white': true,
    };
    smileIDFnMap: Map<string, () => void>;

    constructor() {}

    ngOnInit(): void {}

    setMetaMapMetaData() {
        this.metaData = `{"reference": "${this.metaMapReference}", "verificationType": "${MetaMapType.LIVELINESS}"}`;
    }

    handleUserFinishedEvent() {
        this.emitFinishedMetamapVerification.emit();
    }

    initialize() {
        this.setMetaMapMetaData();
        this.setSmileIDFunctionMap();
    }

    setSmileIDFunctionMap() {
        this.smileIDFnMap = new Map<string, () => void>();
        this.smileIDFnMap.set(this.TAKE_SELFIE, this.takeSelfie);
        this.smileIDFnMap.set(this.REQUEST_CAMERA_ACCESS, this.startSmileIDVerification);
        this.smileIDFnMap.set(this.RE_TAKE_SELFIE, this.retakeSelfie);
        this.smileIDFnMap.set(this.SELECTED_SELFIE, this.useSelectedSelfie);
    }

    startIdentityVerification() {
        if (this.submitting) {
            return;
        }
        this.isSmileIdentityProvider() ? this.isSmileID(true) : this.startMetamapVerification();
    }

    handleSmileID() {
        this.useSmileID ? this.handleSmileIDVerification() : this.isSmileID(true);
    }

    handleSmileIDVerification() {
        const fn = this.smileIDFnMap.get(this.btnText);
        if (!fn) {
            return;
        }
        fn();
    }

    startSmileIDVerification = () => {
        if (!this.requestCameraAccessBtnHTMLElement) {
            return;
        }
        try {
            this.requestCameraAccessBtnHTMLElement.click();
        } catch (e) {
            console.log(' startSmileIDVerification error ---> ', e);
        }
    };

    takeSelfie = () => {
        if (!this.startImageCaptureBtnHTMLElement) {
            return;
        }
        this.startImageCaptureBtnHTMLElement.click();
    };

    useSelectedSelfie = () => {
        if (!this.selectSelfieBtnHTMLElement) {
            return;
        }
        this.selectSelfieBtnHTMLElement.click();
    };

    retakeSelfie = () => {
        if (!this.restartImageCaptureBtnHTMLElement) {
            return;
        }
        this.restartImageCaptureBtnHTMLElement.click();
    };

    isSmileID(isSmileID: boolean) {
        this.useSmileID = isSmileID;
    }

    startMetamapVerification() {}

    isSmileIdentityProvider() {
        return this.identityProvider === IdentityProvider.SMILE_ID;
    }

    handleExitedEvent() {
        this.emitCancel.emit();
    }

    handleSmileIDImage($event: SmileIdImage[]) {
        this.resetParameters();
        this.resetWidget();
        this.emitFinishedSmileIDVerification.emit($event);
    }

    handleRequestScreenHTMLElement($event: HTMLElement) {
        if (!$event) {
            return;
        }
        this.isReview = false;
        const pTag = $event.getElementsByTagName('p');
        const sectionClasses = $event.getElementsByClassName('section');
        this.restyleSectionClasses(sectionClasses);
        this.restylePTag(pTag);
    }

    resetWidget() {
        this.hideUIElement = false;
        this.useSmileID = false;
        this.isReview = false;
        this.useSubmitBtnOnly = false;
    }

    resetParameters() {
        this.title = 'Face Verification';
        this.btnText = 'Continue';
    }

    handleCameraScreenHTMLElement($event: HTMLElement) {
        if (!$event) {
            return;
        }
        this.btnText = this.TAKE_SELFIE;
        this.title = 'Take Selfie';
        this.isReview = false;
        const h1Tag = $event.getElementsByTagName('h1');
        this.restyleH1Tag(h1Tag);
        const sectionClasses = $event.getElementsByClassName('section');
        this.restyleSectionClasses(sectionClasses);
        this.hideUIElement = true;
    }

    handleReviewScreenHTMLElement($event: HTMLElement) {
        if (!$event) {
            return;
        }
        this.btnText = this.SELECTED_SELFIE;
        this.title = 'Review Selfie';
        this.isReview = true;
        const h1Tag = $event.getElementsByTagName('h1');
        this.restyleH1Tag(h1Tag);
        const sectionClasses = $event.getElementsByClassName('section');
        this.restyleSectionClasses(sectionClasses);
    }

    restyleSectionClasses(sectionClasses: HTMLCollectionOf<Element>) {
        for (let i = 0; i < sectionClasses.length; i++) {
            sectionClasses[i].classList?.remove('section');
        }
    }

    restyleH1Tag(h1Tag: HTMLCollectionOf<HTMLHeadingElement>) {
        for (let i = 0; i < h1Tag.length; i++) {
            h1Tag[i].style.display = 'none';
        }
    }

    restylePTag(pTag: HTMLCollectionOf<HTMLParagraphElement>) {
        for (let i = 0; i < pTag.length; i++) {
            pTag[i].style.fontWeight = '400';
            pTag[i].style.fontSize = '12px';
            pTag[i].style.color = '#000';
            pTag[i].style.lineHeight = '17.76px';
        }
    }

    setRequestCameraAccessBtnHTMLElement($event: HTMLElement) {
        if (!$event) {
            return;
        }
        $event.style.display = 'none';
        this.btnText = this.REQUEST_CAMERA_ACCESS;
        this.useSubmitBtnOnly = true;
        this.requestCameraAccessBtnHTMLElement = $event;
    }

    setStartImageCaptureBtnHTMLElement($event: HTMLElement) {
        if (!$event) {
            return;
        }
        $event.style.display = 'none';
        this.startImageCaptureBtnHTMLElement = $event;
    }

    setRestartImageCaptureBtnHTMLElement($event: HTMLElement) {
        if (!$event) {
            return;
        }
        $event.style.display = 'none';
        this.restartImageCaptureBtnHTMLElement = $event;
    }

    setSelectSelfieBtnHTMLElement($event: HTMLElement) {
        if (!$event) {
            return;
        }
        $event.style.display = 'none';
        this.selectSelfieBtnHTMLElement = $event;
    }

    goBack() {
        if (this.submitting) {
            return;
        }
        this.emitBack.emit();
    }

    dismiss() {
        if (this.submitting) {
            return;
        }
        this.emitCancel.emit();
    }
}
